import * as Sentry from "@sentry/react";

/**
 * Operation categories for tracking critical flows in the application.
 */
export type OperationCategory =
  | "payment"
  | "contract"
  | "investigation"
  | "tenant"
  | "landlord"
  | "property"
  | "organization";

interface TrackOperationOptions {
  /** Additional tags to attach to the span */
  tags?: Record<string, string>;
  /** Whether this is a critical operation (affects error level) */
  critical?: boolean;
}

/**
 * Tracks a critical operation with Sentry performance monitoring.
 * Creates a span for the operation and captures any errors with enhanced context.
 *
 * @example
 * ```ts
 * // In a mutation or async operation
 * const result = await trackCriticalOperation(
 *   'create_lease_contract',
 *   'contract',
 *   async () => {
 *     return await createLeaseContract(data);
 *   },
 *   { critical: true }
 * );
 * ```
 */
export async function trackCriticalOperation<T>(
  operationName: string,
  category: OperationCategory,
  fn: () => Promise<T>,
  options: TrackOperationOptions = {},
): Promise<T> {
  const { tags = {}, critical = false } = options;

  return Sentry.startSpan(
    {
      name: operationName,
      op: category,
      attributes: {
        "operation.critical": critical,
        "operation.category": category,
        ...tags,
      },
    },
    async (span) => {
      try {
        const result = await fn();

        // Mark span as successful
        span.setStatus({ code: 1, message: "ok" });

        // Add measurement for successful operations
        Sentry.setMeasurement(`${category}.success`, 1, "none");

        return result;
      } catch (error) {
        // Mark span as failed
        span.setStatus({ code: 2, message: "error" });

        // Capture exception with enhanced context
        Sentry.withScope((scope) => {
          scope.setTag("operation", operationName);
          scope.setTag("category", category);
          scope.setTag("critical", String(critical));

          // Add custom tags
          Object.entries(tags).forEach(([key, value]) => {
            scope.setTag(key, value);
          });

          // Set error level based on criticality
          scope.setLevel(critical ? "fatal" : "error");

          Sentry.captureException(error);
        });

        throw error;
      }
    },
  );
}

/**
 * Adds a custom breadcrumb for tracking user actions in critical flows.
 *
 * @example
 * ```ts
 * addFlowBreadcrumb('contract', 'User started contract creation', { contractType: 'lease' });
 * ```
 */
export function addFlowBreadcrumb(
  category: OperationCategory,
  message: string,
  data?: Record<string, string | number | boolean>,
): void {
  Sentry.addBreadcrumb({
    category: `flow.${category}`,
    message,
    level: "info",
    data,
  });
}

/**
 * Sets user context with additional business-specific tags.
 * Call this after user data is loaded.
 *
 * @example
 * ```ts
 * setUserContext({
 *   id: user.id,
 *   email: user.email,
 *   organizationId: user.organization_id,
 *   membershipPlan: user.membership?.plan,
 * });
 * ```
 */
export function setUserContext(userData: {
  id?: string | number;
  email?: string;
  username?: string;
  organizationId?: string | number;
  membershipPlan?: string;
  organizationType?: string;
}): void {
  // Set Sentry user
  Sentry.setUser({
    id: userData.id?.toString(),
    email: userData.email,
    username: userData.username,
  });

  // Set business context tags
  if (userData.organizationId) {
    Sentry.setTag("organization_id", userData.organizationId.toString());
  }

  if (userData.membershipPlan) {
    Sentry.setTag("membership_plan", userData.membershipPlan);
  }

  if (userData.organizationType) {
    Sentry.setTag("organization_type", userData.organizationType);
  }
}

/**
 * Clears all user context and business tags. Call this on logout.
 * This ensures no cross-user contamination of Sentry data between sessions.
 */
export function clearUserContext(): void {
  Sentry.setUser(null);

  // Clear business context tags to prevent cross-user contamination
  Sentry.setTag("organization_id", undefined);
  Sentry.setTag("membership_plan", undefined);
  Sentry.setTag("organization_type", undefined);

  // Clear additional context
  Sentry.setContext("user_profile", null);
}

/**
 * Captures a business error with structured context.
 * Use this for domain-specific errors that need extra context.
 *
 * @example
 * ```ts
 * // Payment processing failed
 * captureBusinessError(
 *   new Error('Payment declined'),
 *   'payment',
 *   {
 *     contractId: 'contract_123',
 *     paymentMethod: 'card',
 *     errorCode: 'INSUFFICIENT_FUNDS',
 *   }
 * );
 *
 * // Contract validation failed
 * captureBusinessError(
 *   error,
 *   'contract',
 *   { contractType: 'lease', step: 'validation' }
 * );
 * ```
 */
export function captureBusinessError(
  error: Error | unknown,
  category: OperationCategory,
  context: Record<string, string | number | boolean>,
): void {
  Sentry.withScope((scope) => {
    scope.setTag("error_type", "business");
    scope.setTag("category", category);

    scope.setContext("business_context", context);

    Sentry.captureException(error);
  });
}

/**
 * Sets the current contract context for error tracking.
 * Call this when a user is working within a specific contract.
 *
 * @example
 * ```ts
 * setContractContext({
 *   contractId: contract.id,
 *   contractType: contract.contract_type,
 *   contractStatus: contract.status,
 * });
 * ```
 */
export function setContractContext(context: {
  contractId: string | number;
  contractType?: string;
  contractStatus?: string;
}): void {
  Sentry.setContext("contract", {
    contract_id: context.contractId,
    contract_type: context.contractType,
    contract_status: context.contractStatus,
  });

  Sentry.setTag("contract_id", context.contractId.toString());
}

/**
 * Clears the contract context.
 */
export function clearContractContext(): void {
  Sentry.setContext("contract", null);
  Sentry.setTag("contract_id", undefined);
}

/**
 * Sets the current property context for error tracking.
 * Call this when a user is working within a specific property.
 *
 * @example
 * ```ts
 * setPropertyContext({
 *   propertyId: property.id,
 *   propertyType: property.property_type,
 * });
 * ```
 */
export function setPropertyContext(context: {
  propertyId: string | number;
  propertyType?: string;
}): void {
  Sentry.setContext("property", {
    property_id: context.propertyId,
    property_type: context.propertyType,
  });

  Sentry.setTag("property_id", context.propertyId.toString());
}

/**
 * Clears the property context.
 */
export function clearPropertyContext(): void {
  Sentry.setContext("property", null);
  Sentry.setTag("property_id", undefined);
}

/**
 * Sets the current tenant context for error tracking.
 * Call this when a user is working with a specific tenant.
 *
 * @example
 * ```ts
 * setTenantContext({
 *   tenantId: tenant.id,
 *   investigationId: tenant.investigation_id,
 * });
 * ```
 */
export function setTenantContext(context: {
  tenantId: string | number;
  investigationId?: string | number;
}): void {
  Sentry.setContext("tenant", {
    tenant_id: context.tenantId,
    investigation_id: context.investigationId,
  });

  Sentry.setTag("tenant_id", context.tenantId.toString());
}

/**
 * Clears the tenant context.
 */
export function clearTenantContext(): void {
  Sentry.setContext("tenant", null);
  Sentry.setTag("tenant_id", undefined);
}
